import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiUpload } from '../shared/apiClient';
import type { Odl } from '../shared/types';
import { useToast } from '../hooks/useToast';
import { toRomeDateTimeInput } from '../shared/tz';
import { compressImages } from '../utils/imageCompress';
import {
  ArrowLeft,
  Clock,
  FileText,
  Package,
  Camera,
  Plus,
  Trash2,
  CheckCircle,
  AlertTriangle,
  X,
} from 'lucide-react';

type MaterialeRow = {
  nome: string;
  quantita: string;
  unita: string;
};

export function OdlReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const now = useMemo(() => new Date(), []);
  const [inizioLavoroAt, setInizioLavoroAt] = useState(() => toRomeDateTimeInput(now));
  const [fineLavoroAt, setFineLavoroAt] = useState(() => toRomeDateTimeInput(now));
  const [descrizioneLavori, setDescrizioneLavori] = useState('');
  const [materiali, setMateriali] = useState<MaterialeRow[]>([
    { nome: '', quantita: '1', unita: 'N.' },
  ]);
  const [fotoFiles, setFotoFiles] = useState<File[]>([]);
  const [statoFinale, setStatoFinale] = useState<'da_terminare' | 'concluso'>('da_terminare');

  const { data: odlRes, isLoading } = useQuery<{ ok: boolean; data: Odl }>({
    queryKey: ['odl-detail', id],
    queryFn: async () => {
      const res = await apiGet<{ ok: boolean; data: Odl }>(`/odl/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const odl = odlRes?.data;

  const handleAddMateriale = () => {
    setMateriali((prev) => [...prev, { nome: '', quantita: '1', unita: 'N.' }]);
  };

  const handleRemoveMateriale = (index: number) => {
    setMateriali((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMateriale = (index: number, field: keyof MaterialeRow, value: string) => {
    setMateriali((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleFotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const compressi = await compressImages(files);
    setFotoFiles((prev) => [...prev, ...compressi]);
  };

  const removeFoto = (index: number) => {
    setFotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const reportMutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('inizioLavoroAt', inizioLavoroAt);
      fd.append('fineLavoroAt', fineLavoroAt);
      fd.append('descrizioneLavori', descrizioneLavori.trim());
      fd.append('statoFinale', statoFinale);

      const matFiltrati = materiali
        .filter((m) => m.nome.trim() !== '')
        .map((m) => ({
          nome: m.nome.trim(),
          quantita: m.quantita.trim(),
          unita: m.unita.trim(),
        }));
      fd.append('materiali', JSON.stringify(matFiltrati));

      for (const f of fotoFiles) {
        fd.append('foto', f);
      }

      return apiUpload(`/odl/${id}/report`, fd);
    },
    onSuccess: () => {
      toast({
        type: 'success',
        message: statoFinale === 'concluso' ? 'ODL concluso con successo!' : 'Report di giornata salvato! ODL in "Da Terminare".',
      });
      queryClient.invalidateQueries({ queryKey: ['odl-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['odl-squadra-list'] });
      navigate('/odl');
    },
    onError: (err: any) => {
      toast({
        type: 'error',
        message: err.response?.data?.errore || err.message || 'Errore salvataggio report',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!descrizioneLavori.trim()) {
      toast({ type: 'error', message: 'Inserisci le attività svolte in giornata' });
      return;
    }
    reportMutation.mutate();
  };

  if (isLoading || !odl) {
    return (
      <div className="text-center py-16">
        <div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(`/odl/${id}`)}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4" /> Annulla
        </button>

        <span className="text-xs font-bold text-sky-800 bg-sky-100 px-2 py-0.5 rounded">
          GIORNATA #{((odl.reports?.length || 0) + 1)}
        </span>
      </div>

      <div className="card-section">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
          Ordine Committente: {odl.numeroOrdineCommittente}
        </div>
        <div className="text-sm font-bold text-slate-900 mt-0.5">
          <span className="text-emerald-700">[{odl.impianto.codice}]</span> {odl.impianto.nome}
        </div>
      </div>

      {/* 1. Orari */}
      <div className="card-section space-y-3">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-wider">
          <Clock className="w-4 h-4 text-sky-600" />
          Orario Lavori della Giornata
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Inizio Lavoro *
            </label>
            <input
              type="datetime-local"
              className="w-full text-xs p-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-sky-500"
              value={inizioLavoroAt}
              onChange={(e) => setInizioLavoroAt(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Fine Lavoro *
            </label>
            <input
              type="datetime-local"
              className="w-full text-xs p-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-sky-500"
              value={fineLavoroAt}
              onChange={(e) => setFineLavoroAt(e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      {/* 2. Attività Svolte */}
      <div className="card-section space-y-2">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-wider">
          <FileText className="w-4 h-4 text-sky-600" />
          Attività Eseguite Oggi *
        </div>

        <textarea
          rows={4}
          className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-sky-500"
          placeholder="Descrivi liberamente le lavorazioni eseguite nella giornata..."
          value={descrizioneLavori}
          onChange={(e) => setDescrizioneLavori(e.target.value)}
          required
        />
      </div>

      {/* 3. Materiali Impiegati */}
      <div className="card-section space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-wider">
            <Package className="w-4 h-4 text-sky-600" />
            Materiali di Cantiere Utilizzati
          </div>
          <button
            type="button"
            onClick={handleAddMateriale}
            className="text-xs font-bold text-sky-700 flex items-center gap-1 bg-sky-50 px-2 py-1 rounded-md border border-sky-200"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi Riga
          </button>
        </div>

        <div className="space-y-2">
          {materiali.map((m, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Nome materiale (es. Cavo FG7OR 4x1.5, Plinto, Pozzetto...)"
                className="flex-1 text-xs p-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-sky-500"
                value={m.nome}
                onChange={(e) => handleUpdateMateriale(idx, 'nome', e.target.value)}
              />

              <select
                className="w-16 text-xs p-2 rounded-lg border border-slate-300 bg-white"
                value={m.unita}
                onChange={(e) => handleUpdateMateriale(idx, 'unita', e.target.value)}
              >
                <option value="N.">N.</option>
                <option value="MT.">MT.</option>
                <option value="KG.">KG.</option>
                <option value="MQ.">MQ.</option>
                <option value="MC.">MC.</option>
              </select>

              <input
                type="text"
                placeholder="Q.tà"
                className="w-16 text-xs p-2 rounded-lg border border-slate-300 bg-white text-center"
                value={m.quantita}
                onChange={(e) => handleUpdateMateriale(idx, 'quantita', e.target.value)}
              />

              {materiali.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveMateriale(idx)}
                  className="p-1.5 text-slate-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 4. Foto Cantiere */}
      <div className="card-section space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-wider">
            <Camera className="w-4 h-4 text-sky-600" />
            Foto Cantiere di Giornata
          </div>

          <label className="cursor-pointer text-xs font-bold text-sky-700 flex items-center gap-1 bg-sky-50 px-2.5 py-1 rounded-md border border-sky-200">
            <Plus className="w-3.5 h-3.5" /> Scatta / Carica
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFotoChange}
            />
          </label>
        </div>

        {fotoFiles.length > 0 && (
          <div className="grid grid-cols-3 gap-2 pt-1">
            {fotoFiles.map((file, idx) => {
              const url = URL.createObjectURL(file);
              return (
                <div key={idx} className="relative aspect-video rounded-lg overflow-hidden border border-slate-300">
                  <img src={url} alt={`Foto ${idx}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeFoto(idx)}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 5. Stato Destinazione Cantiere */}
      <div className="card-section space-y-3">
        <div className="text-xs font-bold text-slate-800 uppercase tracking-wider">
          Esito Cantiere a Fine Turno *
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setStatoFinale('da_terminare')}
            className={`p-3 rounded-xl border text-left flex flex-col justify-between gap-2 transition-all ${
              statoFinale === 'da_terminare'
                ? 'border-amber-500 bg-amber-50 text-amber-900 ring-2 ring-amber-400'
                : 'border-slate-200 bg-white text-slate-700'
            }`}
          >
            <div className="flex items-center justify-between w-full">
              <span className="text-xs font-bold">DA TERMINARE</span>
              <AlertTriangle className={`w-4 h-4 ${statoFinale === 'da_terminare' ? 'text-amber-600' : 'text-slate-400'}`} />
            </div>
            <p className="text-[11px] text-slate-500 leading-snug">
              Il cantiere non è finito e proseguirà nei turni successivi.
            </p>
          </button>

          <button
            type="button"
            onClick={() => setStatoFinale('concluso')}
            className={`p-3 rounded-xl border text-left flex flex-col justify-between gap-2 transition-all ${
              statoFinale === 'concluso'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-400'
                : 'border-slate-200 bg-white text-slate-700'
            }`}
          >
            <div className="flex items-center justify-between w-full">
              <span className="text-xs font-bold">LAVORI CONCLUSI</span>
              <CheckCircle className={`w-4 h-4 ${statoFinale === 'concluso' ? 'text-emerald-600' : 'text-slate-400'}`} />
            </div>
            <p className="text-[11px] text-slate-500 leading-snug">
              Lavorazione completata definitivamente su questo impianto.
            </p>
          </button>
        </div>
      </div>

      {/* 6. Pulsante di Invio Inline */}
      <div className="pt-2 pb-16 space-y-2">
        <button
          type="submit"
          disabled={reportMutation.isPending}
          className={`w-full py-4 px-4 text-white font-extrabold text-sm rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 ${
            statoFinale === 'concluso'
              ? 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 shadow-emerald-600/30'
              : 'bg-amber-600 hover:bg-amber-700 active:bg-amber-800 shadow-amber-600/30'
          }`}
        >
          {reportMutation.isPending ? (
            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <CheckCircle className="w-5 h-5" />
          )}
          <span>
            {statoFinale === 'concluso'
              ? '✓ Invia Report & Concludi ODL'
              : 'Salva Report & Sposta in "Da Terminare"'}
          </span>
        </button>

        <p className="text-[11px] text-center text-slate-500 font-medium">
          {statoFinale === 'concluso'
            ? 'La scheda verrà archiviata come completata.'
            : 'L\'ODL rimarrà aperto per i successivi interventi della squadra.'}
        </p>
      </div>
    </form>
  );
}
